'use client';

import { VenueCategoryFragment } from '@/__generated__/graphql';
import { EventTime } from '@/lib/parsers/common/parseEventTime';
import { useDayjs } from '@/lib/utils/useDayjs';
import { useVivaticketUrl } from '@/lib/utils/useVivaticketUrl';
import { useTranslations } from 'next-intl';
import Btn, { BtnProps } from '../ui/Btn';
import Txt from '../ui/Txt';

type PageTicketButtonProps = BtnProps<
  'a',
  {
    time: EventTime | null;
    venueCategory: VenueCategoryFragment | null | undefined;
    eventId: string | null | undefined;
  }
>;

export default function PageTicketButton({
  eventId,
  venueCategory,
  time,
  ...props
}: PageTicketButtonProps) {
  const t = useTranslations('common');

  if (!time) return null;

  const { soldout, date, eventTimeId, status, waitlist, isPast } = time;

  const isWaitlistOnly = soldout && waitlist;
  const isDisabled = !time || isPast || (soldout && !waitlist);

  const href = useVivaticketUrl({
    params: {
      E: 'N',
      QL: { value: 'S' + eventId, joiner: '|' },
      V: { value: venueCategory?.venueShorthand, joiner: '|', equater: '' },
      P: { value: time?.eventTimeId, joiner: '|', equater: '' },
    },
    type: isWaitlistOnly ? 'waitlist' : 'purchase',
    ...(isWaitlistOnly &&
      eventId != null &&
      time?.eventTimeId != null && {
        waitlistIds: {
          eventId: String(eventId),
          eventTimeId: String(time.eventTimeId),
          // VenID for waitlist URL; must be set on the venue category in Craft (e.g. COH, PLA) to match Vivaticket
          venueShorthand: String(venueCategory?.venueShorthand ?? 'COH'),
        },
      }),
  });

  if (!status) return null;

  const inner = (
    <Txt key={eventTimeId} as="p" variant="body" cx={{ mT: 'none', mB: 'none' }}>
      <Txt
        as="span"
        style={{
          minWidth: '9.375rem',
          display: 'inline-block',
        }}>
        {useDayjs(date).format('ddd, DD MMM YYYY')}
      </Txt>
      <Txt as="span">{useDayjs(date).format('hh:mma')}</Txt>
    </Txt>
  );

  if (soldout && !waitlist) {
    return (
      <Btn key={eventTimeId} variant="filled" color="black" size="large" disabled disableHover>
        {inner}
        <Txt
          variant="tiny"
          color="error"
          uppercase
          cx={{ position: 'absolute', whiteSpace: 'normal' }}
          style={{
            maxWidth: '3rem',
            top: '50%',
            right: '0.125rem',
            transform: 'rotate(-30deg) translateY(-50%)',
          }}>
          {t('soldout')}
        </Txt>
      </Btn>
    );
  }

  if (isWaitlistOnly) {
    return (
      <Btn key={eventTimeId} color="black" size="large" href={href} {...props}>
        {inner}
        <Txt
          variant="tiny"
          color="white"
          uppercase
          cx={{ position: 'absolute', whiteSpace: 'normal' }}
          style={{
            maxWidth: '4rem',
            top: '40%',
            right: '0rem',
            transform: 'rotate(-30deg) translateY(-50%)',
            textAlign: 'center',
          }}>
          {t('joinWaitlist')}
        </Txt>
      </Btn>
    );
  }

  if (isDisabled) {
    return (
      <Btn key={eventTimeId} color="cream" size="large" disabled disableHover>
        {inner}
      </Btn>
    );
  }

  return (
    <Btn key={eventTimeId} color="black" size="large" href={href} {...props}>
      {inner}
    </Btn>
  );
}
